/**
 * PLT-5741: shared types + vocabulary for per-message chat feedback.
 *
 * Lives in `src/common` so the UI (the vote control + reason popover) and the
 * backend (request validation + Slack card) agree on the rating values, the
 * preset reason chips, and the request shape without duplicating the strings.
 */

/** Direction of a vote. Matches assistant-ui's `submittedFeedback.type`. */
export type ChatFeedbackRating = 'positive' | 'negative';

/**
 * Preset reason chips offered on a 👎. The `id` is the stable value persisted
 * and validated server-side; the `label` is the display string. Keep the two
 * decoupled so we can reword a label without a data migration.
 */
export interface ChatFeedbackReasonChip {
  id: string;
  label: string;
}

export const CHAT_FEEDBACK_REASON_CHIPS: readonly ChatFeedbackReasonChip[] = [
  { id: 'incorrect', label: 'Incorrect' },
  { id: 'incomplete', label: 'Incomplete' },
  { id: 'off_topic', label: 'Off-topic' },
] as const;

export const CHAT_FEEDBACK_REASON_CHIP_IDS: readonly string[] = CHAT_FEEDBACK_REASON_CHIPS.map(c => c.id);

/** Upper bound on the free-text reason line — generous for a sentence or two. */
export const CHAT_FEEDBACK_REASON_TEXT_MAX_LENGTH = 1000;

/**
 * Body of `POST /api/chat/feedback`.
 *
 * `rating: null` clears an existing vote (the user un-toggled their thumb).
 * `messageIndex` + `conversationId` identify the assistant turn being rated —
 * the same coordinates the turn is persisted under (role is always assistant).
 */
export interface ChatFeedbackRequest {
  conversationId: string;
  messageIndex: number;
  rating: ChatFeedbackRating | null;
  reasonChips?: string[];
  reasonText?: string | null;
  /** Snapshots of the rated turn, best-effort from the client for context. */
  userQuestion?: string | null;
  answerText?: string | null;
}
